import { PlayingAsIndicator } from '../PlayingAsIndicator/PlayingAsIndicator'
import { OmniModeAutoControl } from '../OmniModeAutoControl/OmniModeAutoControl'
import { OmniPlayerCountControl } from '../OmniPlayerCountControl/OmniPlayerCountControl'
import { OmniModeLabel } from '../OmniModeLabel/OmniModeLabel'
import type { MateListVM } from '../../../../deps/ui/mateListVM/mateListVM.types'

export interface OmniModeControlsProps {
  isOmniMode: boolean
  hasActiveGame: boolean

  /** Room-wide: true while the room has any Omni mate, regardless of which client added it. Shows the read-only label (see OmniModeLabel) for a client that cannot control the mode itself. */
  roomHasOmniMates: boolean

  /** Shows the Omni Mode toggle below, on or off; works before a game has started too. */
  canUseOmniMode?: boolean
  onToggleOmniMode?: (enabled: boolean) => void

  currentPlayerId?: string
  omniMode: MateListVM['omniMode']

  autoModeChecked: boolean
  onAutoModeChange?: (checked: boolean) => void

  /** See mateListVM.ts's playerCountOptions/omniPlayerCount - hidden by this component whenever there's one or zero options. */
  playerCountOptions: number[]
  playerCount: number
  onSetPlayerCount?: (count: number) => void

  getPlayerColor: (playerIndex: number) => { primary: string; foreground: string } | null
}

/**
 * OmniModeControls groups all top-of-sidebar controls that are specific to Omni mode:
 * - The "Omni Mode" label itself: an on/off toggle for whichever client may use it (see
 *   canUseOmniMode), or plain read-only text for every other client while the room is in
 *   Omni mode (see roomHasOmniMates) - either way with an info button explaining it
 * - A player-count dropdown (only for the client that can control the mode, and only
 *   when the active game supports more than one player count - see playerCountOptions)
 * - "Playing as ..." indicator and Auto toggle, so any connected client can pick which
 *   Omni mate it's acting as (only when the **room** is in Omni mode and **has an
 *   active game** - see roomHasOmniMates)
 *
 * This keeps `MateList` focused on rendering teams, while still letting us evolve Omni-mode UX
 * in a single place.
 */
export function OmniModeControls({
  isOmniMode,
  hasActiveGame,
  roomHasOmniMates,
  canUseOmniMode,
  onToggleOmniMode,
  currentPlayerId,
  omniMode,
  autoModeChecked,
  onAutoModeChange,
  playerCountOptions,
  playerCount,
  onSetPlayerCount,
  getPlayerColor,
}: OmniModeControlsProps) {
  const shouldUsePlayerColors = omniMode.shouldUsePlayerColors.value
  const currentPlayerIndex = omniMode.currentPlayerIndex.value

  return (
    <>
      {canUseOmniMode && onToggleOmniMode ? (
        <OmniModeLabel interactive checked={isOmniMode} onToggle={onToggleOmniMode} />
      ) : roomHasOmniMates ? (
        <OmniModeLabel interactive={false} checked={roomHasOmniMates} />
      ) : null}

      {isOmniMode && onSetPlayerCount && playerCountOptions.length > 1 && (
        <OmniPlayerCountControl value={playerCount} options={playerCountOptions} onChange={onSetPlayerCount} />
      )}

      {roomHasOmniMates && hasActiveGame && (
        <>
          {currentPlayerId && (
            <PlayingAsIndicator
              currentPlayerId={currentPlayerId}
              shouldUsePlayerColors={shouldUsePlayerColors}
              currentPlayerIndex={currentPlayerIndex}
              getPlayerColor={getPlayerColor}
            />
          )}

          <OmniModeAutoControl
            checked={autoModeChecked}
            onChange={(checked) => onAutoModeChange?.(checked)}
            shouldUsePlayerColors={shouldUsePlayerColors}
            currentPlayerIndex={currentPlayerIndex}
            getPlayerColor={getPlayerColor}
          />
        </>
      )}
    </>
  )
}
